import { apiClient } from './api'
import type { AlertConfig, TriggeredAlert, AlertCreateDto } from '@/types/alert.types'

export interface AlertListResponse {
  alerts: AlertConfig[]
  total: number
}

export interface TriggeredAlertListResponse {
  alerts: TriggeredAlert[]
  total: number
}

const mapAlertConfig = (alert: any): AlertConfig => ({
  id: alert.id,
  alertName: alert.alert_name,
  deviceType: alert.device_type,
  measurementColumn: alert.measurement_column,
  comparisonOperator: alert.comparison_operator,
  thresholdValue: Number(alert.threshold_value),
  severityLevel: alert.severity_level,
  alertMessage: alert.alert_message,
  alertLinkedUsers: alert.alert_linked_users ?? false,
  isActive: alert.is_active ?? true,
  deviceCount: alert.device_count || 0,
  recipientCount: alert.recipient_count || 0,
  paramLabel: alert.param_label,
  unit: alert.unit,
  createdAt: alert.created_at,
  updatedAt: alert.updated_at
})

const mapTriggeredAlert = (alert: any): TriggeredAlert => ({
  id: alert.id,
  alertId: alert.alert_id,
  alertName: alert.alert_name,
  deviceId: alert.device_id,
  deviceName: alert.device_name,
  deviceSerialNumber: alert.device_serial_number || '',
  accountId: alert.account_id,
  accountName: alert.account_name,
  measurementColumn: alert.measurement_column,
  triggeredValue: alert.triggered_value ? Number(alert.triggered_value) : 0,
  alertMessage: alert.alert_message,
  severityLevel: alert.severity_level,
  triggeredAt: alert.triggered_at,
  isDismissed: alert.is_dismissed,
  dismissedAt: alert.dismissed_at,
  dismissedBy: alert.dismissed_by,
  paramLabel: alert.param_label,
  unit: alert.unit
})

export const alertService = {
  getAlerts: async (): Promise<AlertListResponse> => {
    const { data } = await apiClient.get('/alerts')
    return {
      alerts: (data.alerts || []).map(mapAlertConfig),
      total: data.total || 0
    }
  },

  getAlert: async (alertId: string): Promise<AlertConfig> => {
    const { data } = await apiClient.get(`/alerts/${alertId}`)
    return mapAlertConfig(data)
  },

  createAlert: async (alert: AlertCreateDto): Promise<AlertConfig> => {
    const { data } = await apiClient.post('/alerts', alert)
    return mapAlertConfig(data)
  },

  updateAlert: async (alertId: string, alert: Partial<AlertCreateDto>): Promise<AlertConfig> => {
    const { data } = await apiClient.put(`/alerts/${alertId}`, alert)
    return mapAlertConfig(data)
  },

  deleteAlert: async (alertId: string): Promise<void> => {
    await apiClient.delete(`/alerts/${alertId}`)
  },

  getTriggeredAlerts: async (dismissed?: boolean): Promise<TriggeredAlertListResponse> => {
    const { data } = await apiClient.get('/alerts/triggered/list', {
      params: { dismissed }
    })
    return {
      alerts: (data.alerts || []).map(mapTriggeredAlert),
      total: data.total || 0
    }
  },

  dismissAlert: async (triggeredAlertId: string): Promise<void> => {
    await apiClient.post(`/alerts/triggered/${triggeredAlertId}/dismiss`)
  },

  // Alert device assignments
  getAlertDevices: async (alertId: string): Promise<{ id: string; name: string; serialNumber: string; deviceType: string }[]> => {
    const { data } = await apiClient.get(`/alerts/${alertId}/devices`)
    return (data.devices || data || []).map((d: any) => ({
      id: d.id,
      name: d.name,
      serialNumber: d.serial_number || d.serialNumber,
      deviceType: d.device_type || d.deviceType,
    }))
  },

  assignDevice: async (alertId: string, deviceId: string): Promise<void> => {
    await apiClient.post(`/alerts/${alertId}/devices/${deviceId}`)
  },

  unassignDevice: async (alertId: string, deviceId: string): Promise<void> => {
    await apiClient.delete(`/alerts/${alertId}/devices/${deviceId}`)
  },

  // Alert recipient assignments
  getAlertRecipients: async (alertId: string): Promise<{ id: string; firstName: string; lastName: string; email: string }[]> => {
    const { data } = await apiClient.get(`/alerts/${alertId}/recipients`)
    return (data.recipients || data || []).map((u: any) => ({
      id: u.id,
      firstName: u.first_name || u.firstName,
      lastName: u.last_name || u.lastName,
      email: u.email,
    }))
  },

  assignRecipient: async (alertId: string, userId: string): Promise<void> => {
    await apiClient.post(`/alerts/${alertId}/recipients/${userId}`)
  },

  unassignRecipient: async (alertId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/alerts/${alertId}/recipients/${userId}`)
  }
}
